import { getAnalytics } from "@tm/insurance/analytics";
import { navigationActions } from "@tm/insurance/navigation";
import { PASSPORT_DETAILS_PAGE_ID } from "@tm/isomorphic-utils";
import {
  AddPassportDetail,
  FlexComp,
  FormattedText,
  H5Tag,
  ImageComponent,
  LandingButtonWhite,
  ManageInsurance,
  OrderDetails,
  ParaTag,
  PersuasionBanner,
  TmNPSFeedback,
} from "@tm/static";
import { ExperimentTrackingString, useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
// import "pwa-mmt-ui-header/ssr/header.css";
import React, { useContext, useEffect, useRef } from "react";

import {
  initializePageTracking,
  trackEvent,
} from "@tm/insurance/analytics/xdm";
import { isPartnerMMT } from "@tm/insurance/utils";
import { usePlanType } from "@tm/ui";
import { parsePriceToNumber } from "@tm/utility";
import PersuasionBg from "../../icons/persuasionBannerBg.svg";
import { persuasionData } from "./mockData";
import {
  FlightUpcomingWrap,
  ThankYouPageHead,
  ThankYouPageHeadWrap,
  ThankYouPageLeft,
  ThankYouPageRight,
  ThankYouPageStyle,
  ThankYouPageWrap,
} from "./ThankYouPage.style";
import { OrderConfirmedPageProps } from "./types";

const persuasionBannerMob = {
  data: persuasionData,
  bgImgURI: PersuasionBg.src,
  btnTitle: "Apply Now",
  title: "Congratulations",
  info: `Your are now eligible for <em>Lifetime Free </em>
      <b>Multi Currency Forex Card </b>`,
};

const analytics = getAnalytics();

const ThankYouPage = ({ data }: OrderConfirmedPageProps) => {
  const device = useBreakpointV2();
  const { type } = usePlanType();
  const isRenderThankYouPage = useRef(false);

  const {
    title,
    info,
    infoIcon,
    persuasionBanner,
    addPassportDetailData,
    manageInsData,
    orderDetails,
    headerIcon,
    downloadPolicy,
    searchDetails,
  } = data;

  const experiment = useContext(ExperimentTrackingString) || "";

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const bookingId = queryParams.get("bookingId");

    analytics?.trackPageLoadEvent(
      {
        bookingId: bookingId || "",
        pageName: "THANK_YOU_POLICY",
        experiment,
      },
      type
    );
    if (!isRenderThankYouPage.current) {
      initializePageTracking(
        {
          page_context: {
            page_name: "thankyou",
            funnel_step: "thankyou",
            page_type: "funnel",
            page_url: window.location.href,
          },
          event_detail: {
            booking_info: {
              booking_id: bookingId || "",
              status: "success",
              price:
                searchDetails?.price ||
                parsePriceToNumber(data?.orderDetails?.orderDetailAmt[0]?.info),
            },
            ...(searchDetails?.paymentType && {
              payment_details: {
                payment_mode: searchDetails?.paymentType || "",
              },
            }),
            components: {
              ...(searchDetails?.productId && {
                product_list: [
                  {
                    id: searchDetails?.productId || "",
                  },
                ],
              }),
            },
          },
        },
        {
          countries: searchDetails?.routes?.map((route) => ({
            code: route.countryCode || "",
            name: route.countryName || "",
            locusId: route.locusId,
            locusType: route.locusType,
          })),
          fromDateTime: searchDetails?.date?.from,
          toDateTime: searchDetails?.date?.to,
          journeyType: searchDetails?.segmentType,
          travellers: searchDetails?.travellers,
        }
      );
      isRenderThankYouPage.current = true;
    }
  }, []);

  const navigateToDownloadPolicy = () => {
    const queryParameters = new URLSearchParams(window.location.search);
    const bookingId = queryParameters.get("bookingId");
    const data = {
      bookingId,
    };
    analytics?.trackThankYouEvents(
      {
        pageName: "THANK_YOU_POLICY",
        actionType: "download_policy_clicked",
      },
      "pageView",
      type
    );

    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "download_policy_clicked",
      },
    });

    return navigationActions.navigateToDownloadPolicy(data);
  };

  const goToPassportScreen = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const bookingId = queryParams.get("bookingId");

    analytics?.trackThankYouEvents(
      {
        pageName: "THANK_YOU_POLICY",
        actionType: "add_passport_clicked",
      },
      "pageView",
      type
    );

    return navigationActions.navigateToAddPassport({
      bookingId,
      pageId: PASSPORT_DETAILS_PAGE_ID.PAYMENT_SUCCESS,
    });
  };

  const onFeedbackCloseAnalytics = () => {
    analytics?.trackThankYouEvents(
      {
        pageName: "THANK_YOU_POLICY",
        actionType: "nps_form_closed",
      },
      "pageView",
      type
    );
  };

  const onSubmitFeedbackAnalytics = (feedbackData: any) => {
    analytics?.trackThankYouEvents(
      {
        pageName: "THANK_YOU_POLICY",
        actionType: "nps_submitted",
      },
      "pageView",
      type
    );

    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: feedbackData?.rating
          ? `nps_submitted_rating_${feedbackData?.rating}`
          : "nps_submitted",
      },
    });

    analytics?.trackThankYouEvents(
      {
        pageName: "THANK_YOU_POLICY",
        actionType: `nps_rating_${feedbackData?.rating}`,
      },
      "pageView",
      type
    );
  };

  const getBookingId = () => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("bookingId");
  };

  return (
    <React.Fragment>
      <ThankYouPageHeadWrap id={"pageId"}>
        <ThankYouPageHead>
          <FlexComp
            margin={device == "lg" ? "0 0 8px" : "0 0 30px"}
            alignItems="center"
          >
            <H5Tag color={theme.yellowText}>
              <FormattedText value={title} />
            </H5Tag>
            {infoIcon && device == "lg" && (
              <ImageComponent
                src={infoIcon}
                height="15px"
                width="15px"
                margin="0 7px 0"
              />
            )}
          </FlexComp>
          <ParaTag
            fontSize={device == "lg" ? "md" : "xs"}
            color={device == "lg" ? theme.blackText : theme.greyText}
          >
            <FormattedText value={info} />
          </ParaTag>
          <FlightUpcomingWrap>
            <ImageComponent
              src={headerIcon}
              height={device == "lg" ? "115px" : "85px"}
              width={device == "lg" ? "225px" : "150px"}
            />
          </FlightUpcomingWrap>
        </ThankYouPageHead>
      </ThankYouPageHeadWrap>
      <ThankYouPageStyle>
        <ThankYouPageWrap>
          <ThankYouPageLeft>
            <OrderDetails
              orderDetails={orderDetails}
              margin="0 0 16px"
              navigateToDownloadPolicy={navigateToDownloadPolicy}
              downloadPolicy={downloadPolicy}
            />
            {persuasionBanner && (
              <PersuasionBanner
                heading={persuasionBanner.title}
                bgImage={persuasionBanner.bgImgURI}
                btnText={persuasionBanner.btnTitle}
                bulletPoints={
                  device == "lg"
                    ? persuasionBanner.data
                    : persuasionBannerMob.data
                }
                btnHandler={() =>
                  window.location.href.includes?.(".goibibo.com")
                    ? window.open(
                        "https://tripmoney.goibibo.com/partners/gi/forex/listing?utm_source=insurance_gi&utm_medium=desktop",
                        "_blank"
                      )
                    : window.open(
                        "https://www.makemytrip.com/forex/?utm_source=tripmoney&utm_medium=insurance-responsive",
                        "_blank"
                      )
                }
                info={persuasionBannerMob.info}
                // crossSell={crossSell}
              />
            )}
          </ThankYouPageLeft>
          <ThankYouPageRight>
            {device == "lg" && downloadPolicy.isEnabled ? (
              <LandingButtonWhite
                btnLeftIcon={downloadPolicy?.leftIconUrl}
                btnRightIcon={downloadPolicy?.rightIconUrl}
                customTitle={downloadPolicy?.buttonTitle}
                customWidth="100%"
                onClick={navigateToDownloadPolicy}
              />
            ) : null}
            {device == "lg" && addPassportDetailData && (
              <AddPassportDetail
                data={addPassportDetailData}
                handlePassportBtn={goToPassportScreen}
              />
            )}
            <ManageInsurance
              title={manageInsData.title}
              info={manageInsData.info}
              orderBtn={manageInsData.orderBtn}
              handleManageOrder={() => {
                analytics?.trackThankYouEvents(
                  {
                    pageName: "THANK_YOU_POLICY",
                    actionType: "my_orders_clicked",
                  },
                  "pageView",
                  type
                );
                trackEvent({
                  event_detail: {
                    event_type: "action",
                    event_name: "component_cta-clicked",
                    event_value: "my_orders_clicked",
                  },
                });
                if (manageInsData?.orderBtnLink) {
                  window.location.href = manageInsData?.orderBtnLink;
                } else {
                  navigationActions.navigateToOrderListing();
                }
              }}
              orderBtnIcon={manageInsData?.orderBtnIcon}
              goHomeText={manageInsData?.goHomeBtn}
              goHomeBtnIcon={manageInsData?.goHomeBtnIcon}
            />
          </ThankYouPageRight>

          {isPartnerMMT() && (
            <TmNPSFeedback
              bookingId={getBookingId()}
              onFeedbackCloseAnalytics={onFeedbackCloseAnalytics}
              onSubmitFeedbackAnalytics={onSubmitFeedbackAnalytics}
            />
          )}
        </ThankYouPageWrap>
      </ThankYouPageStyle>
    </React.Fragment>
  );
};

export default ThankYouPage;
